import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

function StaffScheduleManager({ staffId, staffName, onClose }) {
  const [schedule, setSchedule] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTimeOffForm, setShowTimeOffForm] = useState(false);

  const [timeOffForm, setTimeOffForm] = useState({
    start_date: '',
    end_date: '',
    reason: 'vacation',
    notes: ''
  });

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  const defaultSchedule = daysOfWeek.map(day => ({
    day_of_week: day.value,
    is_working: day.value !== 0 && day.value !== 3, // Default: Off on Sun & Wed
    start_time: '09:00',
    end_time: '17:00',
    break_start: '12:00',
    break_end: '13:00'
  }));

  useEffect(() => {
    loadSchedule();
  }, [staffId]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/staff-schedules/${staffId}`);
      
      if (response.data.success) {
        const loadedSchedule = response.data.schedules;
        setTimeOff(response.data.time_off || []);
        
        // Merge loaded schedule with defaults
        const mergedSchedule = daysOfWeek.map(day => {
          const existing = loadedSchedule.find(s => s.day_of_week === day.value);
          return existing || defaultSchedule[day.value];
        });
        
        setSchedule(mergedSchedule);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading schedule:', err);
      setSchedule(defaultSchedule);
      setLoading(false);
    }
  };

  const updateDaySchedule = (dayIndex, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[dayIndex] = {
      ...newSchedule[dayIndex],
      [field]: value
    };
    setSchedule(newSchedule);
  };

  const handleSaveSchedule = async () => {
    try {
      const response = await axios.post('/api/staff-schedules', {
        staff_id: staffId,
        schedules: schedule
      });

      if (response.data.success) {
        alert('✅ Schedule saved successfully!');
      }
    } catch (err) {
      console.error('Error saving schedule:', err);
      alert('❌ Error saving schedule');
    }
  };

  const handleRequestTimeOff = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post('/api/staff-schedules/time-off', {
        staff_id: staffId,
        ...timeOffForm
      });

      if (response.data.success) {
        alert('✅ Time off requested!');
        setShowTimeOffForm(false);
        setTimeOffForm({ start_date: '', end_date: '', reason: 'vacation', notes: '' });
        loadSchedule();
      }
    } catch (err) {
      console.error('Error requesting time off:', err);
      alert('❌ Error requesting time off');
    }
  };

  const handleApproveTimeOff = async (id, status) => {
    try {
      const response = await axios.put(`/api/staff-schedules/time-off/${id}`, {
        status,
        admin_notes: status === 'approved' ? 'Approved by admin' : 'Denied by admin'
      });

      if (response.data.success) {
        alert(`✅ Time off ${status}!`);
        loadSchedule();
      }
    } catch (err) {
      console.error('Error updating time off:', err);
      alert('❌ Error updating time off');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-2xl text-slate-400">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-surface rounded-lg max-w-4xl w-full p-6 my-8 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            📅 Schedule: {staffName}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white text-3xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Weekly Schedule */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-white mb-4">Weekly Schedule</h3>
          <div className="space-y-3">
            {schedule.map((day, index) => (
              <div key={index} className="bg-background p-4 rounded-lg">
                <div className="grid grid-cols-6 gap-4 items-center">
                  <div className="col-span-1">
                    <span className="text-white font-semibold">
                      {daysOfWeek[day.day_of_week].label}
                    </span>
                  </div>
                  
                  <div className="col-span-1">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={day.is_working}
                        onChange={(e) => updateDaySchedule(index, 'is_working', e.target.checked)}
                        className="w-5 h-5"
                      />
                      <span className="text-slate-300">Working</span>
                    </label>
                  </div>

                  {day.is_working ? (
                    <>
                      <div className="col-span-2">
                        <div className="flex gap-2 items-center">
                          <input
                            type="time"
                            value={day.start_time || '09:00'}
                            onChange={(e) => updateDaySchedule(index, 'start_time', e.target.value)}
                            className="bg-surface border border-slate-700 text-white p-2 rounded w-full"
                          />
                          <span className="text-slate-400">to</span>
                          <input
                            type="time"
                            value={day.end_time || '17:00'}
                            onChange={(e) => updateDaySchedule(index, 'end_time', e.target.value)}
                            className="bg-surface border border-slate-700 text-white p-2 rounded w-full"
                          />
                        </div>
                      </div>

                      <div className="col-span-2">
                        <div className="flex gap-2 items-center">
                          <span className="text-slate-400 text-sm">Break:</span>
                          <input
                            type="time"
                            value={day.break_start || ''}
                            onChange={(e) => updateDaySchedule(index, 'break_start', e.target.value)}
                            className="bg-surface border border-slate-700 text-white p-2 rounded w-full"
                            placeholder="Start"
                          />
                          <span className="text-slate-400">-</span>
                          <input
                            type="time"
                            value={day.break_end || ''}
                            onChange={(e) => updateDaySchedule(index, 'break_end', e.target.value)}
                            className="bg-surface border border-slate-700 text-white p-2 rounded w-full"
                            placeholder="End"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-4 text-slate-500 text-sm">
                      Day off
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={handleSaveSchedule}
              className="flex-1 bg-primary hover:bg-purple-600 text-white py-3 rounded-lg font-semibold"
            >
              ✅ Save Schedule
            </button>
          </div>
        </div>

        {/* Time Off Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">Time Off Requests</h3>
            <button
              onClick={() => setShowTimeOffForm(!showTimeOffForm)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
            >
              + Request Time Off
            </button>
          </div>

          {showTimeOffForm && (
            <form onSubmit={handleRequestTimeOff} className="bg-background p-4 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-slate-300 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={timeOffForm.start_date}
                    onChange={(e) => setTimeOffForm({...timeOffForm, start_date: e.target.value})}
                    className="w-full bg-surface border border-slate-700 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-300 mb-2">End Date</label>
                  <input
                    type="date"
                    value={timeOffForm.end_date}
                    onChange={(e) => setTimeOffForm({...timeOffForm, end_date: e.target.value})}
                    className="w-full bg-surface border border-slate-700 text-white p-2 rounded"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-slate-300 mb-2">Reason</label>
                <select
                  value={timeOffForm.reason}
                  onChange={(e) => setTimeOffForm({...timeOffForm, reason: e.target.value})}
                  className="w-full bg-surface border border-slate-700 text-white p-2 rounded"
                >
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-slate-300 mb-2">Notes (optional)</label>
                <textarea
                  value={timeOffForm.notes}
                  onChange={(e) => setTimeOffForm({...timeOffForm, notes: e.target.value})}
                  className="w-full bg-surface border border-slate-700 text-white p-2 rounded h-20"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-purple-600 text-white py-2 rounded font-semibold"
                >
                  Submit Request
                </button>
                <button
                  type="button"
                  onClick={() => setShowTimeOffForm(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Time Off List */}
          <div className="space-y-2">
            {timeOff.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No time off requests
              </div>
            ) : (
              timeOff.map(request => (
                <div key={request.id} className="bg-background p-4 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-white font-semibold">
                        {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                      </div>
                      <div className="text-slate-400 text-sm capitalize">{request.reason}</div>
                      {request.notes && (
                        <div className="text-slate-500 text-sm mt-1">{request.notes}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        request.status === 'approved' ? 'bg-emerald-900 text-emerald-300' :
                        request.status === 'denied' ? 'bg-red-900 text-red-300' :
                        'bg-amber-900 text-amber-300'
                      }`}>
                        {request.status.toUpperCase()}
                      </span>
                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApproveTimeOff(request.id, 'approved')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded text-xs"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => handleApproveTimeOff(request.id, 'denied')}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                          >
                            ✗ Deny
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StaffScheduleManager;
